import BorderedButton from '@components/BorderedButton';
import Button from '@components/Button';
import Icon from '@components/Icon';
import PageLoading from '@components/PageLoading';
import VipWrapper from '@components/VipWrapper';
import IconExpand from '@components/icons/IconExpand';
import { HOST, VIP_URL } from '@constants';
import { arrow, offset, useFloating } from '@floating-ui/react-dom';
import { useTabMain } from '@hooks/paramsHooks';
import useToggle from '@hooks/useToggle';
import { $templateId, $tourData, $tourDataApi } from '@stores/dataStores';
import { $isVip } from '@stores/memberStores';
import { $panelApi } from '@stores/panelStores';
import type { EditConfigUI, TemplateId } from '@types';
import { getTemplateConfig } from '@utils/templateConfig';
import {} from 'effector';
import { useStore } from 'effector-react';
import { delay } from 'pano-utils';
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { saveData } from '../../edit';
import { IntroModal } from '../IntroModal';
import { ShareConfigModal } from '../ShareConfigModal';
import TemplateModal from '../TemplateModal';
import preview from '../actions/preview';
import { TEMPLATES_CONFIG } from '../constants';
import ImgBack from './img-back.png';
import ImgPreview from './img-preview.png';
import ImgSave from './img-save.png';
import ImgShare from './img-share.png';
import ImgTemplate from './img-template.png';

const TIP_ID = '720_template_tip';

const uiMap = new Map<TemplateId, EditConfigUI>();

function Header() {
  const match = useMatch('/:mainTab/*');
  const templateId = useStore($templateId);
  const isVip = useStore($isVip);
  const loading = useStore(saveData.pending);
  const templateModal = useToggle(false);
  const shareModal = useToggle(false);
  const navigate = useNavigate();
  const isSwiting = useToggle(false);
  const tip = useToggle(!localStorage.getItem(TIP_ID));
  const arrowRef = useRef(null);

  const { x, y, reference, floating, strategy, middlewareData } = useFloating({
    placement: 'bottom',
    middleware: [offset(10), arrow({ element: arrowRef })],
    strategy: 'fixed',
  });
  const { x: arrowX } = (middlewareData.arrow ?? {}) as {
    x: number;
    centerOffset: number;
  };

  const tabMain = useTabMain();
  const [sParams] = useSearchParams();

  const activeIndex = match?.params.mainTab === 'sandtable' ? 1 : 0;

  const turnOffTip = () => {
    tip.off();
    localStorage.setItem(TIP_ID, 'true');
  };

  const handleTemplateClick = () => {
    templateModal.on();
    if (tip.isOn) {
      turnOffTip();
    }
  };

  const handleTemplateChange = async (id: TemplateId) => {
    isSwiting.on();
    $panelApi.dismiss();

    const currentUI = $tourData.getState().config?.ui;
    if (currentUI) {
      uiMap.set(templateId, currentUI);
    }

    navigate('main/basic');
    $tourDataApi.update((draft) => {
      draft.templateId = id;
      const ui = uiMap.get(id);
      if (ui) {
        draft.config.ui = ui;
      } else {
        draft.config.ui = getTemplateConfig(id)('uiColor') || undefined;
      }
    });
    await delay(2000);
    isSwiting.off();
  };

  const handleTabChange = (i: number) => {
    const url = ['/main', '/sandtable'][i];
    if (i === 1) {
      navigate(`${url}?tab=${tabMain ?? 'basic'}`);
    } else {
      navigate(`${url}/${sParams.get('tab') ?? 'basic'}`);
    }
  };

  return (
    <div className="relative flex h-30 items-center justify-center">
      <Button
        scheme="darker"
        className="flex-shrink-0"
        as="a"
        href={`${HOST}/my/720vr/tour`}
      >
        <Icon src={ImgBack} />
        <div className="ml-5">退出</div>
      </Button>
      <Button
        ref={reference}
        scheme="darker"
        className="ml-10 flex-shrink-0"
        onClick={handleTemplateClick}
      >
        <Icon src={ImgTemplate} />
        <div className="ml-5">{`模板：${TEMPLATES_CONFIG[templateId].name}`}</div>
        <IconExpand className="ml-10" active={false} />
      </Button>
      {tip.isOn && (
        <div
          ref={floating}
          className="z-30 flex items-center rounded bg-orange p-[6px] text-sm text-white"
          style={{
            position: strategy,
            top: y ?? '',
            left: x ?? '',
          }}
          onClick={turnOffTip}
        >
          点击切换作品界面模板
          <div
            ref={arrowRef}
            className="absolute -top-5 h-10 w-10 rotate-45 transform bg-orange"
            style={{ left: arrowX }}
          />
        </div>
      )}
      {isVip === false && (
        <BorderedButton
          scheme="orange"
          className="ml-10 flex-shrink-0"
          as="a"
          href={VIP_URL}
          target="_blank"
        >
          <VipWrapper showVip vipPosition="start">
            <div className="ml-5">开通会员服务，解锁全部模板功能</div>
          </VipWrapper>
        </BorderedButton>
      )}
      <div className="absolute inline-flex items-center justify-center">
        <Button
          scheme={activeIndex === 0 ? 'blue' : 'darker'}
          onClick={() => handleTabChange(0)}
        >
          基础设置
        </Button>
        <Button
          className="ml-10"
          scheme={activeIndex === 1 ? 'blue' : 'darker'}
          onClick={() => handleTabChange(1)}
        >
          沙盘编辑
        </Button>
      </div>
      <Button
        scheme="darker"
        className="ml-auto flex-shrink-0"
        disabled={loading}
        onClick={() => shareModal.on()}
      >
        <Icon src={ImgShare} />
        <div className="ml-5">分享</div>
      </Button>
      <Button
        scheme="darker"
        className="ml-10 flex-shrink-0"
        disabled={loading}
        onClick={() => preview()}
      >
        <Icon src={ImgPreview} />
        <div className="ml-5">预览</div>
      </Button>
      <Button
        className="ml-10 flex-shrink-0"
        disabled={loading}
        onClick={() => saveData()}
      >
        <Icon src={ImgSave} />
        <div className="ml-5">保存</div>
      </Button>
      {templateModal.isOn && (
        <TemplateModal
          templateId={templateId}
          onConfirm={handleTemplateChange}
          onRequestClose={templateModal.off}
        />
      )}
      {isSwiting.isOn &&
        createPortal(<PageLoading text="模板切换中" />, document.body)}
      <IntroModal />
      {shareModal.isOn && <ShareConfigModal onRequestClose={shareModal.off} />}
    </div>
  );
}

export default Header;
